// components/common/ActionKebabMenu.jsx
import React, { Component } from "react";

/**
 * Props:
 * - placement: "row" | "inline"  (default: "row")
 * - items: Array<{ key, label, onClick, disabled?, danger?, icon? }>
 * - triggerLabel: string (aria-label)
 * - triggerClassName: string (extra classes for button)
 * - buttonTitle: string (title tooltip)
 * - disabled: boolean (disable trigger)
 * - renderTriggerChildren?: () => ReactNode (custom trigger content; defaults to ⋮)
 * - menuSide?: "right" | "below" (default: "right")
 */
export default class ActionKebabMenu extends Component {
  state = { open: false };
  _root = null;

  componentDidMount() {
    document.addEventListener("mousedown", this.handleDocClick, true);
    document.addEventListener("keydown", this.handleKeyDown, true);
  }
  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleDocClick, true);
    document.removeEventListener("keydown", this.handleKeyDown, true);
  }

  handleDocClick = (e) => {
    if (!this.state.open) return;
    if (!this._root) return;
    if (!this._root.contains(e.target)) this.setState({ open: false });
  };
  handleKeyDown = (e) => {
    if (e.key === "Escape" && this.state.open) this.setState({ open: false });
  };

  toggle = () => {
    if (this.props.disabled) return;
    this.setState((s) => ({ open: !s.open }));
  };
  close = () => this.setState({ open: false });

  render() {
    const {
      placement = "row",
      items = [],
      triggerLabel = "More actions",
      triggerClassName = "",
      buttonTitle,
      disabled = false,
      renderTriggerChildren,
      menuSide = "right", // "right" | "below"
    } = this.props;

    const { open } = this.state;

    const anchorClass =
      "kebab-anchor " +
      (placement === "inline" ? "kebab-anchor--inline" : "kebab-anchor--row");

    const menuSideClass =
      "kebab-menu " + (menuSide === "below" ? "kebab-menu--below" : "kebab-menu--right");

    return (
      <div className={anchorClass} ref={(el) => (this._root = el)}>
        <button
          type="button"
          className={`kebab-trigger ${triggerClassName || ""}`}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={triggerLabel}
          title={buttonTitle || triggerLabel}
          onClick={this.toggle}
          disabled={disabled}
        >
          {typeof renderTriggerChildren === "function" ? (
            renderTriggerChildren()
          ) : (
            <span className="icon" aria-hidden>
              ⋮
            </span>
          )}
        </button>

        {open && !disabled && (
          <div
            className={menuSideClass}
            role="menu"
            onClick={(e) => e.stopPropagation()} // let outside click close; keep inside clicks local
          >
            {items.length === 0 ? (
              <div className="kebab-menu-empty">No actions</div>
            ) : (
              items.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  role="menuitem"
                  className={
                    "kebab-menu-item" +
                    (it.danger ? " kebab-menu-item-danger" : "") +
                    (it.disabled ? " kebab-menu-item-disabled" : "")
                  }
                  disabled={!!it.disabled}
                  onClick={() => {
                    if (it.disabled) return;
                    try {
                      it.onClick?.();
                    } finally {
                      this.close();
                    }
                  }}
                >
                  {it.icon && (
                    <span className="icon" aria-hidden>
                      {it.icon}
                    </span>
                  )}
                  <span>{it.label}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    );
  }
}
